import { Alert, Avatar, Box, Button, Grid, Link, MenuItem, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { TrackedTextField } from "src/theme/StyledFields";
import { getAuth } from "../allauth/lib/allauth";
import ApiClient from "../config/ApiClient";
import { isUnder13 } from "../utils/coppa";
import ChangeCellPhoneDialog from "./ChangeCellPhoneDialog";

type ProfileData = {
    first_name: string;
    last_name: string;
    gender: string | null;
    ethnicity: string[] | null;
    birthdate: string | null; // yyyy-mm-dd
    zip_code: string | null;
    bio: string | null;
    photo?: string | null;  // URL from GET
    cover_photo?: string | null; // URL from GET
    summary?: string | null;
    emergency_contact?: string | null;
    cell_phone?: string | null;
};

const GENDER_PLACEHOLDER = "__select__";
const genderOptions = [
    { value: GENDER_PLACEHOLDER, label: "Select gender" },
    { value: "", label: "Unspecified" },
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "rather_not_say", label: "Rather Not Say" }
];

const ethnicityOptions = [
    { value: "african_american", label: "Black or African American" },
    { value: "caucasian", label: "White" },
    { value: "american_native", label: "Native American" },
    { value: "asian", label: "Asian" },
    { value: "hispanic_latino", label: "Hispanic or Latino" },
    { value: "other", label: "Other" },
    { value: "prefer_not_to_say", label: "Prefer Not to Say" }
];

export default function ProfileForm() {

    const [data, setData] = useState<ProfileData>({
        first_name: "",
        last_name: "",
        gender: GENDER_PLACEHOLDER,
        ethnicity: [],
        birthdate: "",
        zip_code: "",
        bio: "",
        photo: null,
        emergency_contact: null,
        cell_phone: null,
    });
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    // Cell phone is managed by its own verified flow (ChangeCellPhoneDialog), not saved
    // as part of the main profile submit.
    const [phoneDialogOpen, setPhoneDialogOpen] = useState<boolean>(false);

    const photoPreview = useMemo(() => {
        if (photoFile) return URL.createObjectURL(photoFile);
        return data.photo || null;
    }, [photoFile, data.photo]);

    const coverPhotoPreview = useMemo(() => {
        if (coverPhotoFile) return URL.createObjectURL(coverPhotoFile);
        return data.cover_photo || null;
    }, [coverPhotoFile, data.cover_photo]);

    const ZIP_REGEX = /^\d{5}(-\d{4})?$/;

    function validate(): boolean {
        const errors: Record<string, string> = {};

        const firstName = (data.first_name || "").trim();
        if (!firstName) {
            errors.first_name = "First name is required";
        } else if (firstName.length > 100) {
            errors.first_name = "First name must be 100 characters or less";
        }

        const lastName = (data.last_name || "").trim();
        if (!lastName) {
            errors.last_name = "Last name is required";
        } else if (lastName.length > 100) {
            errors.last_name = "Last name must be 100 characters or less";
        }

        const birthdate = (data.birthdate || "").trim();
        if (!birthdate) {
            errors.birthdate = "Birthdate is required";
        } else {
            const date = new Date(birthdate);
            if (isNaN(date.getTime())) {
                errors.birthdate = "Please enter a valid date";
            } else if (date > new Date()) {
                errors.birthdate = "Birthdate cannot be in the future";
            }
        }

        const zipCode = (data.zip_code || "").trim();
        if (!zipCode) {
            errors.zip_code = "Zip code is required";
        } else if (!ZIP_REGEX.test(zipCode)) {
            errors.zip_code = "Please enter a valid US zip code (e.g. 12345 or 12345-6789)";
        } else if (zipCode.length > 10) {
            errors.zip_code = "Zip code must be 10 characters or less";
        }

        const emergencyContact = (data.emergency_contact || "").trim();
        if (!emergencyContact) {
            errors.emergency_contact = "Emergency contact is required";
        } else if (emergencyContact.length > 500) {
            errors.emergency_contact = "Emergency contact must be 500 characters or less";
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }

    useEffect(() => {
        let mounted = true;
        (async () => {
            const resp = await ApiClient.get<ProfileData>("/api/account/profile");
            if (mounted) {
                if (resp.success && resp.data) {
                    // Normalize fields
                    const d = resp.data as any;
                    const rawGender = d.gender;
                    const gender = ["male", "female", "rather_not_say", ""].includes(rawGender) ? rawGender : GENDER_PLACEHOLDER;
                    setData({
                        first_name: d.first_name || "",
                        last_name: d.last_name || "",
                        gender,
                        ethnicity: Array.isArray(d.ethnicity) ? d.ethnicity : [],
                        birthdate: d.birthdate || "",
                        zip_code: d.zip_code || "",
                        bio: d.bio || "",
                        photo: d.photo || null,
                        cover_photo: d.cover_photo || null,
                        summary: d.summary || null,
                        emergency_contact: d.emergency_contact || null,
                        cell_phone: d.cell_phone || null,
                    });
                } else {
                    setError(resp.error || "Failed to load profile");
                }
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const form = new FormData();
            form.append("first_name", (data.first_name || "").trim());
            form.append("last_name", (data.last_name || "").trim());
            if (data.gender && data.gender !== GENDER_PLACEHOLDER) form.append("gender", data.gender);
            if (data.birthdate) form.append("birthdate", data.birthdate);
            if (data.zip_code) form.append("zip_code", data.zip_code.trim());
            if (data.bio) form.append("bio", data.bio);
            if (data.ethnicity && data.ethnicity.length) form.append("ethnicity", JSON.stringify(data.ethnicity));
            if (data.emergency_contact) form.append("emergency_contact", (data.emergency_contact || "").trim());
            // cell_phone is intentionally omitted: it is updated via the verified
            // ChangeCellPhoneDialog flow, not the main profile save.
            if (data.summary) form.append("summary", data.summary);
            if (photoFile) form.append("photo", photoFile);
            if (coverPhotoFile) form.append("cover_photo", coverPhotoFile);

            const resp = await ApiClient.put<ProfileData>("/api/account/profile", form, {
                "Content-Type": "multipart/form-data"
            });

            if (resp.success && resp.data) {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setSuccess("Profile updated");
                const d = resp.data as any;
                setData((prev) => ({
                    ...prev,
                    photo: d.photo || prev.photo,
                    cover_photo: d.cover_photo || prev.cover_photo,
                    summary: d.summary || prev.summary,
                    cell_phone: d.cell_phone ?? prev.cell_phone,
                }));
                setPhotoFile(null);
                setCoverPhotoFile(null);
                // Refresh the auth session so the onboarding checklist picks up
                // the updated `profile_complete` flag (name + birthdate). Without
                // this, the "Complete your profile" step stays unchecked until a
                // full page reload. Mirrors WaiverModal's post-accept getAuth().
                try {
                    await getAuth();
                } catch (e) {
                    // Non-fatal: profile saved successfully; the session will
                    // refresh on the next navigation/reload.
                }
            } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setError(resp.error || "Failed to save profile");
            }
        } catch (err: any) {
            setError(err?.message || "Unexpected error");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Box component="form" onSubmit={onSubmit} sx={{ maxWidth: 800, mx: "auto", p: 2 }} data-attr="profile-form_submit">
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TrackedTextField
                        fullWidth
                        required
                        label="First Name"
                        value={data.first_name}
                        onChange={(e) => { setData({ ...data, first_name: e.target.value }); setFieldErrors((prev) => ({ ...prev, first_name: "" })); }}
                        error={!!fieldErrors.first_name}
                        helperText={fieldErrors.first_name}
                        inputProps={{ maxLength: 100 }}
                        data-attr="profile-form_field-first-name"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TrackedTextField
                        fullWidth
                        required
                        label="Last Name"
                        value={data.last_name}
                        onChange={(e) => { setData({ ...data, last_name: e.target.value }); setFieldErrors((prev) => ({ ...prev, last_name: "" })); }}
                        error={!!fieldErrors.last_name}
                        helperText={fieldErrors.last_name}
                        inputProps={{ maxLength: 100 }}
                        data-attr="profile-form_field-last-name"
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TrackedTextField
                        select
                        fullWidth
                        label="Gender"
                        value={data.gender ?? ""}
                        onChange={(e) => { setData({ ...data, gender: e.target.value }); setFieldErrors((prev) => ({ ...prev, gender: "" })); }}
                        error={!!fieldErrors.gender}
                        helperText={fieldErrors.gender || "Please select the option that best reflects your gender identity. If you prefer not to share, you may select “Prefer Not to Say.”"}
                        data-attr="profile-form_field-gender"
                    >
                        {genderOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TrackedTextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TrackedTextField
                        select
                        SelectProps={{
                            multiple: true
                        }}
                        fullWidth
                        label="Race"
                        value={data.ethnicity || []}
                        onChange={(e) => {
                            const raw = e.target.value;
                            const next = typeof raw === "string" ? [raw] : (raw as string[]);
                            const PREFER_NOT = "prefer_not_to_say";
                            let ethnicity = next;
                            // Prefer Not to Say is mutually exclusive with all other race options.
                            if (next.includes(PREFER_NOT)) {
                                const hadPreferNot = (data.ethnicity || []).includes(PREFER_NOT);
                                // Newly selected Prefer Not to Say → clear everything else.
                                // Selecting another option while Prefer Not to Say was set → drop it.
                                ethnicity = hadPreferNot
                                    ? next.filter((v) => v !== PREFER_NOT)
                                    : [PREFER_NOT];
                            }
                            setData({ ...data, ethnicity });
                            setFieldErrors((prev) => ({ ...prev, ethnicity: "" }));
                        }}
                        error={!!fieldErrors.ethnicity}
                        helperText={fieldErrors.ethnicity || "Please select the racial identity or identities that best reflect how you identify. If you identify as multiracial, select all options that apply. You may also select “Prefer Not to Say” if you do not wish to disclose."}
                        data-attr="profile-form_field-ethnicity"
                    >
                        {ethnicityOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}

                    </TrackedTextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TrackedTextField
                        fullWidth
                        required
                        label="Birthdate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={data.birthdate || ""}
                        onChange={(e) => { setData({ ...data, birthdate: e.target.value }); setFieldErrors((prev) => ({ ...prev, birthdate: "" })); }}
                        error={!!fieldErrors.birthdate}
                        helperText={
                            fieldErrors.birthdate
                            || (isUnder13(data.birthdate)
                                ? "If you are under 13, you'll need a parent or guardian to continue."
                                : undefined)
                        }
                        data-attr="profile-form_field-birthdate"
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TrackedTextField
                        fullWidth
                        required
                        label="Zip Code"
                        value={data.zip_code || ""}
                        onChange={(e) => { setData({ ...data, zip_code: e.target.value }); setFieldErrors((prev) => ({ ...prev, zip_code: "" })); }}
                        error={!!fieldErrors.zip_code}
                        helperText={fieldErrors.zip_code}
                        inputProps={{ maxLength: 10 }}
                        placeholder="12345 or 12345-6789"
                        data-attr="profile-form_field-zip-code"
                    />
                </Grid>

                <Grid container justifyContent="space-between" item xs={12} spacing={2}>
                    <Grid item xs={12} sm={6} >
                        <Stack spacing={0.5} alignItems="flex-start" sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="subtitle2">Cell Phone</Typography>
                            {data.cell_phone &&
                                <Typography variant="body2" color="text.secondary">
                                    {(data.cell_phone || "").trim()}
                                </Typography>
                            }
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setPhoneDialogOpen(true)}
                                data-attr="profile-form_change-cell-phone"
                            >
                                {(data.cell_phone || "").trim() ? "Change Cell Phone" : "Add Cell Phone"}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                Optional cell number for SMS notifications and sign-in.
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} >
                        <Stack spacing={0.5} alignItems="flex-start" sx={{ p: 1.5, height: '100%', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Link component={RouterLink} to="/account/email" style={{ textDecoration: "underline", textAlign: "center" }}>
                                Change / Add Email-Addresses
                            </Link>
                        </Stack>
                    </Grid>
                </Grid>


                <Grid item xs={12}>
                    <TrackedTextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="About me"
                        value={data.bio || ""}
                        onChange={(e) => setData({ ...data, bio: e.target.value })}
                        data-attr="profile-form_field-bio"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TrackedTextField
                        fullWidth
                        required
                        multiline
                        minRows={3}
                        label="Emergency Contact"
                        value={data.emergency_contact || ""}
                        onChange={(e) => { setData({ ...data, emergency_contact: e.target.value }); setFieldErrors((prev) => ({ ...prev, emergency_contact: "" })); }}
                        error={!!fieldErrors.emergency_contact}
                        helperText={fieldErrors.emergency_contact}
                        inputProps={{ maxLength: 500 }}
                        placeholder="Enter emergency contact information (name, phone, relationship, etc.)"
                        data-attr="profile-form_field-emergency-contact"
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Stack spacing={1} alignItems="flex-start">
                        <Typography variant="subtitle1">Photo</Typography>
                        {photoPreview && <Avatar variant="rounded" src={photoPreview} sx={{ width: 120, height: 120 }} />}
                        <Button variant="outlined" component="label" data-attr="profile-form_photo-upload">
                            Choose File
                            <input type="file" accept="image/*" hidden onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                setPhotoFile(file);
                            }} data-attr="profile-form_field-photo" />
                        </Button>
                    </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Stack spacing={1} alignItems="flex-start">
                        <Typography variant="subtitle1">Cover Photo</Typography>
                        {coverPhotoPreview && (
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: 300,
                                    height: 120,
                                    backgroundImage: `url(${coverPhotoPreview})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            />
                        )}
                        <Button variant="outlined" component="label" data-attr="profile-form_cover-photo-upload">
                            Choose File
                            <input type="file" accept="image/*" hidden onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                setCoverPhotoFile(file);
                            }} data-attr="profile-form_field-cover-photo" />
                        </Button>
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <Button type="submit" variant="contained" disabled={saving} data-attr="profile-form_submit-button">
                        {saving ? "Saving..." : "Save Changes"}
                    </Button>
                </Grid>
            </Grid >

            <ChangeCellPhoneDialog
                open={phoneDialogOpen}
                onClose={() => setPhoneDialogOpen(false)}
                currentPhone={data.cell_phone}
                onSuccess={(phone) => {
                    setData((prev) => ({ ...prev, cell_phone: phone }));
                    setSuccess("Cell phone number verified and saved.");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                }}
            />
        </Box >
    );
}


